import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Stack,
  Paper,
} from '@mui/material';
import {
  Send,
  Notifications,
  People,
  Person,
  Group,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../context/NotificationContext';

const audienceOptions = [
  { value: 'all', label: 'All App Users', icon: <Group fontSize="small" /> },
  { value: 'role', label: 'By Role', icon: <People fontSize="small" /> },
  { value: 'users', label: 'Specific Users', icon: <Person fontSize="small" /> },
];

const roleOptions = [
  { value: 'user', label: 'Customers (User)' },
  { value: 'provider', label: 'Service Providers' },
  { value: 'admin', label: 'Admins' },
];

const initialForm = {
  title: '',
  body: '',
  audience: 'all',
  role: 'user',
  userIds: [],
};

const PushNotifications = () => {
  const { showSuccess, showError } = useNotification();
  const [form, setForm] = useState(initialForm);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [recipientCount, setRecipientCount] = useState(null);
  const [countLoading, setCountLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setResult(null);
  };

  const fetchUsers = useCallback(async (search = '') => {
    setUsersLoading(true);
    try {
      const response = await adminAPI.getUsers({ search, limit: 50, isActive: 'active' });
      const users = (response && response.users) || [];
      setUserOptions(
        users.map((u) => ({
          id: u._id || u.id,
          name: `${u.firstName} ${u.lastName}`,
          email: u.email,
          role: u.role,
        })),
      );
    } catch (err) {
      console.error('Failed to load users', err);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchRecipientCount = useCallback(async () => {
    setCountLoading(true);
    try {
      const params = { audience: form.audience };
      if (form.audience === 'role') params.role = form.role;
      if (form.audience === 'users') {
        params.userIds = form.userIds.map((u) => u.id);
      }
      const response = await adminAPI.getPushAudienceCount(params);
      setRecipientCount(response?.data?.totalRecipients ?? 0);
    } catch (err) {
      setRecipientCount(null);
    } finally {
      setCountLoading(false);
    }
  }, [form.audience, form.role, form.userIds]);

  useEffect(() => {
    if (form.audience === 'users' && form.userIds.length === 0) {
      setRecipientCount(0);
      return;
    }
    fetchRecipientCount();
  }, [fetchRecipientCount, form.audience, form.userIds.length]);

  const handleSend = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      showError('Title and message are required');
      return;
    }

    setSending(true);
    setResult(null);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        audience: form.audience,
      };
      if (form.audience === 'role') payload.role = form.role;
      if (form.audience === 'users') {
        payload.userIds = form.userIds.map((u) => u.id);
      }

      const response = await adminAPI.sendPushNotification(payload);
      const data = response?.data || {};
      setResult(data);
      showSuccess(response?.message || 'Push notification sent successfully');

      if (data.sent > 0) {
        setForm(initialForm);
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || 'Failed to send push notification';
      showError(message);
    } finally {
      setSending(false);
    }
  };

  const canSend =
    form.title.trim() &&
    form.body.trim() &&
    !sending &&
    (form.audience !== 'users' || form.userIds.length > 0);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={1}>
        Push Notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Send broadcast push notifications to mobile app users.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Notifications color="primary" />
                <Typography variant="h6">Compose Notification</Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notification Title"
                    placeholder="e.g. Flash Sale on All Services!"
                    value={form.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    helperText={`${form.title.length}/100`}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Message"
                    placeholder="Write the notification message here..."
                    value={form.body}
                    onChange={(e) => handleChange('body', e.target.value)}
                    inputProps={{ maxLength: 500 }}
                    helperText={`${form.body.length}/500`}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Audience</FormLabel>
                    <RadioGroup
                      row
                      value={form.audience}
                      onChange={(e) => handleChange('audience', e.target.value)}
                    >
                      {audienceOptions.map((opt) => (
                        <FormControlLabel
                          key={opt.value}
                          value={opt.value}
                          control={<Radio />}
                          label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {opt.icon}
                              <span>{opt.label}</span>
                            </Stack>
                          }
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {form.audience === 'role' && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={form.role}
                        label="Role"
                        onChange={(e) => handleChange('role', e.target.value)}
                      >
                        {roleOptions.map((r) => (
                          <MenuItem key={r.value} value={r.value}>
                            {r.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {form.audience === 'users' && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={userOptions}
                      loading={usersLoading}
                      getOptionLabel={(option) =>
                        `${option.name} (${option.email})`
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      value={form.userIds}
                      onChange={(e, newValue) => handleChange('userIds', newValue)}
                      onInputChange={(e, value) => {
                        if (e && e.type === 'change') fetchUsers(value);
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option.id}
                            label={option.name}
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search & select users"
                          placeholder="Type to search users"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {usersLoading ? (
                                  <CircularProgress size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>

              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    sending ? <CircularProgress size={20} color="inherit" /> : <Send />
                  }
                  onClick={handleSend}
                  disabled={!canSend}
                >
                  {sending ? 'Sending...' : 'Send Notification'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <People color="primary" />
                  <Typography variant="h6">Recipients</Typography>
                </Stack>
                <Box display="flex" alignItems="baseline" gap={1}>
                  {countLoading ? (
                    <CircularProgress size={28} />
                  ) : (
                    <Typography variant="h3" fontWeight="bold" color="primary">
                      {recipientCount ?? 0}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    user(s) will receive this notification
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Only app users with push notifications enabled and an active
                  Expo push token are counted.
                </Typography>
              </CardContent>
            </Card>

            {result && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Alert
                  severity={result.sent > 0 ? 'success' : 'warning'}
                  sx={{ mb: 2 }}
                >
                  Delivered to {result.sent} of {result.total} recipient(s)
                  {result.failed > 0 ? ` (${result.failed} failed)` : ''}.
                </Alert>
                {result.recipients && result.recipients.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Recipients:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {result.recipients
                        .slice(0, 20)
                        .map((r) => (
                          <Chip
                            key={r.id}
                            size="small"
                            label={`${r.name} (${r.role})`}
                            variant="outlined"
                          />
                        ))}
                      {result.recipients.length > 20 && (
                        <Chip
                          size="small"
                          label={`+${result.recipients.length - 20} more`}
                        />
                      )}
                    </Stack>
                  </Box>
                )}
              </Paper>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PushNotifications;
